'use client'

import { useCallback, useMemo, useRef } from 'react'

import { useSession } from '@/entities/session'

import { fetchAiChatHistoryViaProxy } from '../api/chat-history'
import { createAiChatSession } from '../api/chat-session'
import { sendChatMessage } from '../api/send-chat-message'
import { AI_SHOW_OPERATOR_KEY, AI_SHOW_PERSONAL_ACCOUNT_KEY } from '../config/config'
import { useAiChatState } from '../context/ai-chat-state-context'
import { getPhoneForChatMessage } from '../lib/get-phone-for-chat-message'
import { createUserMessage } from '../lib/message-factory'

import { useAiChatStatus } from './use-ai-chat-status'

const DEFAULT_HISTORY_PAGE = 1
const DEFAULT_HISTORY_PAGE_SIZE = 20

export const useAiChat = () => {
	const { state, actions } = useAiChatState()
	const { refetchStatus, hasUnreadMessage } = useAiChatStatus()
	const { currentSession, setSessionToken } = useSession()
	const streamRef = useRef<{ close: () => void } | null>(null)

	const {
		setPartial,
		setTitle,
		setStreamStatus,
		setHistory,
		setPrevHistory,
		pushMessage,
		setStreamDelta,
		finalizeStream,
		closeChat,
		setError,
		clearMessages,
		clearError
	} = actions

	const callOperator = useCallback(() => {
		try {
			localStorage.setItem(AI_SHOW_OPERATOR_KEY, 'true')
		} catch {
			// localStorage может быть недоступен
		}
		setPartial({ showOperatorButton: true })
	}, [setPartial])

	const showPersonalAccount = useCallback(() => {
		try {
			localStorage.setItem(AI_SHOW_PERSONAL_ACCOUNT_KEY, 'true')
		} catch {
			// localStorage может быть недоступен
		}
		setPartial({ showPersonalAccountButton: true })
	}, [setPartial])

	const addToBasket = useCallback(
		(value: string[]) => {
			setPartial({ productIdsToCart: value })
		},
		[setPartial]
	)

	const clearProductIdsToCart = useCallback(() => {
		setPartial({ productIdsToCart: [] })
	}, [setPartial])

	const sendMessage = useCallback(
		async (message: string) => {
			// Прерываем предыдущий стрим, если он еще идет
			if (streamRef.current) {
				streamRef.current.close()
				streamRef.current = null
			}
			localStorage.removeItem(AI_SHOW_OPERATOR_KEY)
			localStorage.removeItem(AI_SHOW_PERSONAL_ACCOUNT_KEY)

			const phone = await getPhoneForChatMessage(currentSession?.token ?? '', setSessionToken)

			const userMessage = createUserMessage(message)

			pushMessage(userMessage)
			setPartial({
				isStreaming: true,
				error: null,
				showOperatorButton: false,
				showPersonalAccountButton: false
			})

			try {
				const stream = await sendChatMessage({
					message: message,
					phone,
					onTitleSet: setTitle,
					onMessage: setStreamStatus,
					onDelta: setStreamDelta,
					onFinal: finalizeStream,
					onCloseChat: closeChat,
					onCallOperator: callOperator,
					onShowPersonalAccount: showPersonalAccount,
					onAddToBasket: addToBasket
				})

				streamRef.current = stream
			} catch (err) {
				const msg = err instanceof Error ? err.message : 'Не удалось отправить сообщение'
				setError(msg)
			}
		},
		[
			finalizeStream,
			pushMessage,
			setPartial,
			setStreamDelta,
			setError,
			setStreamStatus,
			setTitle,
			currentSession?.token,
			setSessionToken,
			callOperator,
			showPersonalAccount,
			closeChat,
			addToBasket
		]
	)

	const cancelStream = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.close()
			streamRef.current = null
		}

		setPartial({ isStreaming: false, streamMessage: '', streamStatus: '' })
	}, [setPartial])

	const loadHistory = useCallback(
		async (page = DEFAULT_HISTORY_PAGE, perPage = DEFAULT_HISTORY_PAGE_SIZE) => {
			setPartial({
				isLoading: true,
				error: null,
				streamStatus: '',
				streamMessage: '',
				isStreaming: false,
				showOperatorButton: false,
				showPersonalAccountButton: false
			})
			try {
				const history = await fetchAiChatHistoryViaProxy(page, perPage)
				setHistory(history)

				if (hasUnreadMessage) {
					void refetchStatus()
				}

				if (!history.messages?.length) {
					try {
						localStorage.removeItem(AI_SHOW_OPERATOR_KEY)
						localStorage.removeItem(AI_SHOW_PERSONAL_ACCOUNT_KEY)
					} catch {
						// localStorage может быть недоступен
					}
					return
				}

				try {
					setPartial({
						showOperatorButton: localStorage.getItem(AI_SHOW_OPERATOR_KEY) === 'true',
						showPersonalAccountButton: localStorage.getItem(AI_SHOW_PERSONAL_ACCOUNT_KEY) === 'true'
					})
				} catch {
					// localStorage может быть недоступен
				}
			} catch (err) {
				setError('Не удалось загрузить историю')
			}
		},
		[setPartial, setError, setHistory, refetchStatus, hasUnreadMessage]
	)

	const loadNextPage = useCallback(async () => {
		if (!state?.paging?.has_more) return
		setPartial({ isMoreLoading: true, error: null })
		try {
			const history = await fetchAiChatHistoryViaProxy(state?.paging?.next_page, DEFAULT_HISTORY_PAGE_SIZE)

			setPrevHistory(history)
		} catch (err) {
			setError('Не удалось предыдущие сообщения')
		}
	}, [state?.paging?.page])

	const createChat = useCallback(async () => {
		try {
			localStorage.removeItem(AI_SHOW_OPERATOR_KEY)
			localStorage.removeItem(AI_SHOW_PERSONAL_ACCOUNT_KEY)
		} catch {
			// localStorage может быть недоступен
		}

		setPartial({
			isLoading: true,
			isInitialization: true,
			error: null,
			paging: {},
			title: '',
			showOperatorButton: false,
			showPersonalAccountButton: false
		})
		try {
			await createAiChatSession()
			setPartial({ isLoading: false, isInitialization: false, isActive: true, messages: [] })
		} catch (err) {
			const msg = err instanceof Error ? err.message : 'Не удалось загрузить историю'
			setPartial({ isLoading: false, isInitialization: false })
			setError(msg)
		}
	}, [])

	const setStartText = useCallback((text: string) => setPartial({ startText: text }), [setPartial])

	// Получаем последнее сообщение пользователя для передачи оператору
	const lastUserMessage = useMemo(
		() => [...state.messages].reverse().find(item => item.role === 'user')?.content || '',
		[state.messages]
	)

	return useMemo(
		() => ({
			...state,
			sendMessage,
			clearMessages,
			clearError,
			createChat,
			setStartText,
			loadHistory,
			loadNextPage,
			cancelStream,
			callOperator,
			lastUserMessage,
			addToBasket,
			clearProductIdsToCart
		}),
		[
			state,
			sendMessage,
			clearMessages,
			clearError,
			setStartText,
			loadHistory,
			setError,
			setPartial,
			cancelStream,
			callOperator,
			addToBasket,
			clearProductIdsToCart
		]
	)
}
